// Admin panel: preview + send the weekly "New circles this week" digest.
// Admin clicks Preview to see who and how many will receive it plus an HTML
// preview of the email body, then Send to push the digest to every opted-in
// member. Kept intentionally low-frequency (Sunday-morning cadence).

import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Eye, Users } from "lucide-react";

export default function AdminDigestPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digest/circles/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preview");
    } finally {
      setLoading(false);
    }
  };

  const send = async (force = false) => {
    if (!preview) return;
    const recipients = preview.recipient_count || 0;
    if (!window.confirm(`Send the weekly digest to ${recipients} member${recipients === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/digest/circles/send${force ? "?force=true" : ""}`);
      toast.success(`Digest sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail) {
        if (window.confirm(`${detail}\n\nSend anyway?`)) return send(true);
      } else {
        toast.error(detail || "Could not send digest");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="jj-card p-6 mt-8" data-testid="admin-digest-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline text-burgundy">Admin</div>
          <h3 className="font-serif text-2xl text-jjblue mt-1 leading-tight">Weekly digest — new prayer circles</h3>
          <p className="text-sm text-[var(--jj-muted)] mt-2 max-w-lg">
            A soft Sunday-morning email highlighting prayer circles born from the community feed in the last 7 days. Only opted-in members receive it.
          </p>
        </div>
        <Mail className="h-6 w-6 text-burgundy/60" strokeWidth={1.4} />
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        <button onClick={loadPreview} disabled={loading} className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2" data-testid="digest-preview-btn">
          <Eye className="h-4 w-4" strokeWidth={1.6} /> {loading ? "Loading…" : "Preview"}
        </button>
        <button onClick={() => send(false)} disabled={!preview || sending} className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2" data-testid="digest-send-btn">
          <Send className="h-4 w-4" strokeWidth={1.6} /> {sending ? "Sending…" : "Send now"}
        </button>
      </div>

      {preview && (
        <div className="mt-6" data-testid="digest-preview">
          <div className="flex items-center gap-3 text-sm text-jjblue/90">
            <span className="flex items-center gap-1"><Users className="h-4 w-4" strokeWidth={1.5} /> {preview.recipient_count} recipient{preview.recipient_count === 1 ? "" : "s"}</span>
            <span className="text-[var(--jj-muted)]">·</span>
            <span>{preview.circles.length} new circle{preview.circles.length === 1 ? "" : "s"} this week</span>
          </div>
          {preview.circles.length > 0 && (
            <ul className="mt-3 space-y-1.5 text-sm text-[var(--jj-text-secondary)]" data-testid="digest-preview-list">
              {preview.circles.map((c) => (
                <li key={c.id} className="flex items-start justify-between gap-2 border-b border-[var(--jj-border)] pb-1.5 last:border-0">
                  <div>
                    <span className="text-jjblue">{c.name}</span>
                    {c.spawned_from_post && <span className="ml-2 text-[10px] uppercase tracking-wider text-burgundy">Born from a Feed prayer</span>}
                  </div>
                  <span className="text-[var(--jj-muted)] text-xs whitespace-nowrap">{c.member_count} interceding</span>
                </li>
              ))}
            </ul>
          )}
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-[var(--jj-muted)] hover:text-burgundy">Show rendered HTML</summary>
            <div
              className="mt-3 border border-[var(--jj-border)] rounded-lg overflow-hidden bg-white max-h-[500px] overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: preview.html }}
              data-testid="digest-preview-html"
            />
          </details>
        </div>
      )}
    </section>
  );
}
